import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ConflictError } from '../api/client';
import type { EstimateFile, Shop, ShopStage, ShopVisit, ShopVisitWrite, ShopWrite, Unit } from '../api/types';
import { ConflictBanner, ErrorBanner, Select, SlideOver, formatDate, today } from './ui';

const MAX_PDF_BYTES = 10 * 1024 * 1024;

const fileSize = (n: number) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

/** Opens (saves) one estimate PDF. Goes through the API because the sign-in travels in a header. */
export const openEstimate = (shopVisitId: number, f: EstimateFile) =>
  api.download(`/api/shop-visits/${shopVisitId}/estimates/${f.estimateFileId}`);

/** Where a visit stands, in the words the screens use. */
export const STAGE_LABEL: Record<ShopStage, string> = {
  awaiting_estimate: 'Waiting for estimate',
  awaiting_decision: 'Estimate to approve',
  approved: 'Approved — in repair',
  denied: 'Denied',
  picked_up: 'Picked up',
};

/** The stages that want someone to do something are the ones that stand out. */
export function StageText({ stage }: { stage: ShopStage }) {
  const color = stage === 'awaiting_decision' ? 'var(--orange-text)'
    : stage === 'denied' ? 'var(--red)'
    : stage === 'picked_up' ? 'var(--muted)'
    : 'var(--text)';
  return <span style={{ color, fontWeight: stage === 'awaiting_decision' ? 700 : 600 }}>{STAGE_LABEL[stage]}</span>;
}

export const money = (n?: number | null) =>
  n == null ? '—' : n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

/** A shop as one line: the name, and the town where there are several of it. */
export const shopLabel = (s: { name: string; address?: string | null }) => {
  const town = s.address?.split('\n').pop()?.trim();
  return town ? `${s.name.trim()} — ${town}` : s.name.trim();
};

const invalidateAll = (queryClient: ReturnType<typeof useQueryClient>) => {
  void queryClient.invalidateQueries({ queryKey: ['shops'] });
  void queryClient.invalidateQueries({ queryKey: ['shop-visits'] });
  void queryClient.invalidateQueries({ queryKey: ['unit'] });
};

type Decision = 'none' | 'approved' | 'denied';

/**
 * Logging a visit, or moving one along: the estimate comes in, it is approved
 * or denied, the unit is picked up. Opened from the Shops screen or from a
 * unit's own page, where the unit is already chosen.
 */
export function VisitForm({ visit, unitId, onClose }: { visit?: ShopVisit; unitId?: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const shops = useQuery({ queryKey: ['shops'], queryFn: () => api.get<Shop[]>('/api/shops') });
  const units = useQuery({
    queryKey: ['units'],
    queryFn: () => api.get<Unit[]>('/api/vehicles'),
    enabled: !visit && unitId === undefined,
  });

  const [fleetAssetId, setFleetAssetId] = useState<string>(String(visit?.fleetAssetId ?? unitId ?? ''));
  const [shopId, setShopId] = useState<string>(visit ? String(visit.shopId) : '');
  const [addingShop, setAddingShop] = useState(false);
  const [inOn, setInOn] = useState(visit?.inOn ?? (visit ? '' : today()));
  const [issues, setIssues] = useState(visit?.issues ?? '');
  const [estimate, setEstimate] = useState(visit?.estimate != null ? String(visit.estimate) : '');
  const [decision, setDecision] = useState<Decision>(visit?.approvedOn ? 'approved' : visit?.deniedOn ? 'denied' : 'none');
  const [decidedOn, setDecidedOn] = useState(visit?.approvedOn ?? visit?.deniedOn ?? today());
  const [decidedBy, setDecidedBy] = useState(visit?.decidedBy ?? '');
  const [pickedUpOn, setPickedUpOn] = useState(visit?.pickedUpOn ?? '');
  const [notes, setNotes] = useState(visit?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  // The PDFs already on the visit, and one picked here that is sent on Save.
  const [files, setFiles] = useState<EstimateFile[]>(visit?.estimateFiles ?? []);
  const [pdf, setPdf] = useState<File | null>(null);
  const pdfInput = useRef<HTMLInputElement>(null);
  const done = useRef<{ uploaded?: boolean }>({});
  const [dragOver, setDragOver] = useState(false);

  // A file dropped just outside the box would otherwise open in the browser
  // and lose everything typed into the form.
  useEffect(() => {
    const stop = (e: DragEvent) => e.preventDefault();
    window.addEventListener('dragover', stop);
    window.addEventListener('drop', stop);
    return () => { window.removeEventListener('dragover', stop); window.removeEventListener('drop', stop); };
  }, []);

  // Visits logged before PDFs were required can be approved without one; every newer one can't.
  const needsPdf = (visit?.estimateRequired ?? true) && files.length === 0 && pdf === null;
  const wasApproved = !!visit?.approvedOn;

  const pickPdf = (list: FileList | null) => {
    const f = list?.[0];
    if (!f) return;
    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) { setError('Attach the estimate as a PDF.'); return; }
    if (f.size > MAX_PDF_BYTES) { setError('An estimate PDF can be at most 10 MB.'); return; }
    setError(null);
    setPdf(f);
    done.current.uploaded = false;
  };

  const remove = useMutation({
    mutationFn: (f: EstimateFile) => api.del(`/api/shop-visits/${visit!.shopVisitId}/estimates/${f.estimateFileId}`),
    onSuccess: (_, f) => { setFiles((all) => all.filter((x) => x.estimateFileId !== f.estimateFileId)); invalidateAll(queryClient); },
    onError: (e: Error) => setError(e.message),
  });

  const save = useMutation({
    mutationFn: async () => {
      const body: ShopVisitWrite = {
        fleetAssetId: Number(fleetAssetId),
        shopId: Number(shopId),
        inOn: inOn || null,
        issues: issues.trim() || null,
        estimate: estimate === '' ? null : Number(estimate),
        approvedOn: decision === 'approved' ? decidedOn || null : null,
        deniedOn: decision === 'denied' ? decidedOn || null : null,
        decidedBy: decision === 'none' ? null : decidedBy.trim() || null,
        pickedUpOn: pickedUpOn || null,
        notes: notes.trim() || null,
        rowVersionNo: visit?.rowVersionNo,
      };
      // On a visit already logged, the PDF remembers it went up, so Save again
      // after a failed save doesn't attach it twice.
      const attach = async (id: number) => {
        if (!pdf || done.current.uploaded) return;
        await api.upload(`/api/shop-visits/${id}/estimates`, pdf);
        done.current.uploaded = true;
      };
      if (visit) {
        // The PDF goes first, so an approval saved with it finds it there.
        await attach(visit.shopVisitId);
        return api.put(`/api/shop-visits/${visit.shopVisitId}`, body);
      }
      // A new visit goes with its PDF in one request: both are saved, or neither.
      return pdf
        ? api.upload('/api/shop-visits', pdf, { visit: JSON.stringify(body) })
        : api.post('/api/shop-visits', body);
    },
    onSuccess: () => { invalidateAll(queryClient); onClose(); },
    onError: (e: Error) => (e instanceof ConflictError ? setConflict(e.message) : setError(e.message)),
  });

  const unitName = visit?.unit ?? units.data?.find((u) => String(u.fleetAssetId) === fleetAssetId)?.unitNumber;
  const ready = fleetAssetId !== '' && shopId !== '' && !(decision !== 'none' && !decidedOn)
    && !(decision === 'approved' && !wasApproved && needsPdf);
  const activeShops = (shops.data ?? []).filter((s) => s.isActive || String(s.shopId) === shopId);

  if (addingShop) {
    return (
      <ShopForm
        onClose={() => setAddingShop(false)}
        onSaved={(id) => { setShopId(String(id)); setAddingShop(false); }}
      />
    );
  }

  return (
    <SlideOver
      title={visit ? `Shop visit — ${visit.unit}` : unitId !== undefined ? `Send ${unitName ?? 'unit'} to a shop` : 'Log a shop visit'}
      onClose={onClose}
      footer={(
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn--sm" onClick={onClose}>Cancel</button>
          <button className="btn btn--sm btn--primary" disabled={!ready || save.isPending}
            onClick={() => { setError(null); save.mutate(); }}>
            {save.isPending ? 'Saving…' : visit ? 'Save' : 'Log visit'}
          </button>
        </div>
      )}
    >
      {conflict && <ConflictBanner message={conflict} onReload={() => { invalidateAll(queryClient); onClose(); }} />}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {!visit && unitId === undefined && (
        <div style={{ marginBottom: 14 }}>
          <label className="field-label" htmlFor="sv-unit">NAME</label>
          <Select id="sv-unit" className="control" style={{ width: '100%' }} value={fleetAssetId}
            onChange={(e) => setFleetAssetId(e.target.value)}>
            <option value="">Choose…</option>
            {(units.data ?? [])
              .slice()
              .sort((a, b) => (a.unitNumber ?? a.vin ?? '').localeCompare(b.unitNumber ?? b.vin ?? ''))
              .map((u) => (
                <option key={u.fleetAssetId} value={u.fleetAssetId}>
                  {u.unitNumber ?? u.vin}{[u.make, u.model].filter(Boolean).length ? ` — ${[u.modelYear, u.make, u.model].filter(Boolean).join(' ')}` : ''}
                </option>
              ))}
          </Select>
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <label className="field-label" htmlFor="sv-shop">SHOP</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <Select id="sv-shop" className="control" style={{ flex: 1, minWidth: 0 }} value={shopId}
            onChange={(e) => setShopId(e.target.value)}>
            <option value="">Choose…</option>
            {activeShops.map((s) => <option key={s.shopId} value={s.shopId}>{shopLabel(s)}</option>)}
          </Select>
          <button className="btn btn--sm" onClick={() => setAddingShop(true)} title="A shop not on the list yet">+ New shop</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <label className="field-label" htmlFor="sv-in">DATE IN</label>
          <input id="sv-in" type="date" className="control" max={today()} value={inOn} onChange={(e) => setInOn(e.target.value)} />
        </div>
        <div>
          <label className="field-label" htmlFor="sv-est">ESTIMATE</label>
          <input id="sv-est" className="control" style={{ width: 130 }} inputMode="decimal" placeholder="$" value={estimate}
            onChange={(e) => setEstimate(e.target.value.replace(/[^\d.]/g, ''))} />
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label className="field-label" htmlFor="sv-issues">ISSUES</label>
        <textarea id="sv-issues" className="control" rows={3} style={{ width: '100%' }} value={issues}
          placeholder="What's wrong with it" onChange={(e) => setIssues(e.target.value)} />
      </div>

      <div style={{ marginBottom: 14 }}>
        <div className="field-label">ESTIMATE PDF</div>
        {files.map((f) => (
          <div key={f.estimateFileId} style={{ display: 'flex', alignItems: 'baseline', gap: 10, fontSize: 12.5, padding: '3px 0' }}>
            <button type="button" className="ms__link" style={{ fontSize: 12.5, textAlign: 'left', minWidth: 0, overflowWrap: 'anywhere' }}
              onClick={() => void openEstimate(visit!.shopVisitId, f).catch((e: Error) => setError(e.message))}>
              {f.fileName}
            </button>
            <span className="meta" style={{ whiteSpace: 'nowrap' }}>{fileSize(f.sizeBytes)} · {formatDate(f.uploadedAtUtc)}</span>
            <button type="button" className="ms__link" style={{ marginLeft: 'auto', color: 'var(--red)' }} disabled={remove.isPending}
              onClick={() => { if (window.confirm(`Remove ${f.fileName} from this visit?`)) remove.mutate(f); }}>
              Remove
            </button>
          </div>
        ))}
        {pdf && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, fontSize: 12.5, padding: '3px 0' }}>
            <span style={{ color: 'var(--text)', overflowWrap: 'anywhere' }}>{pdf.name}</span>
            <span className="meta" style={{ whiteSpace: 'nowrap' }}>{fileSize(pdf.size)} · attached when you save</span>
            <button type="button" className="ms__link" style={{ marginLeft: 'auto' }} onClick={() => setPdf(null)}>Don't attach</button>
          </div>
        )}
        <input ref={pdfInput} type="file" accept="application/pdf,.pdf" hidden
          onChange={(e) => { pickPdf(e.target.files); e.target.value = ''; }} />
        <div
          className={`dropzone${dragOver ? ' dropzone--over' : ''}`}
          style={{ padding: '14px 12px', marginTop: 4, borderRadius: 'var(--radius-control)' }}
          role="button"
          tabIndex={0}
          onClick={() => pdfInput.current?.click()}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && pdfInput.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); pickPdf(e.dataTransfer.files); }}
        >
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
            {dragOver ? 'Drop the PDF to attach it'
              : <>
                {pdf ? 'Drag a different PDF here to use it instead' : `Drag the ${files.length ? 'revised estimate' : 'estimate'} PDF here`}, or{' '}
                <span style={{ color: 'var(--blue)', fontWeight: 600 }}>choose a file</span>
              </>}
          </div>
        </div>
        {visit && !visit.estimateRequired && files.length === 0 && !pdf && (
          <div className="meta" style={{ marginTop: 6 }}>Logged before PDFs were required — it can be approved without one.</div>
        )}
      </div>

      <div style={{ marginBottom: 14 }}>
        <div className="field-label">THE ESTIMATE</div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12.5, marginBottom: 8 }}>
          {(['none', 'approved', 'denied'] as Decision[]).map((d) => (
            <label key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="radio" name="sv-decision" checked={decision === d} onChange={() => setDecision(d)} />
              {d === 'none' ? 'Not decided yet' : d === 'approved' ? 'Approved' : 'Denied'}
            </label>
          ))}
        </div>
        {decision === 'approved' && !wasApproved && needsPdf && (
          <div className="meta" style={{ color: 'var(--orange-text)', marginBottom: 8 }}>
            Attach the shop's estimate PDF before approving it.
          </div>
        )}
        {decision !== 'none' && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <label className="field-label" htmlFor="sv-decided">{decision === 'approved' ? 'APPROVED ON' : 'DENIED ON'}</label>
              <input id="sv-decided" type="date" className="control" max={today()} value={decidedOn}
                onChange={(e) => setDecidedOn(e.target.value)} />
            </div>
            <div style={{ flex: 1, minWidth: 160 }}>
              <label className="field-label" htmlFor="sv-by">DECIDED BY</label>
              <input id="sv-by" className="control" style={{ width: '100%' }} value={decidedBy} placeholder="e.g. Dan"
                onChange={(e) => setDecidedBy(e.target.value)} />
            </div>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 14 }}>
        <label className="field-label" htmlFor="sv-picked">PICKED UP</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input id="sv-picked" type="date" className="control" max={today()} value={pickedUpOn}
            onChange={(e) => setPickedUpOn(e.target.value)} />
          {!pickedUpOn && <button className="btn btn--sm" onClick={() => setPickedUpOn(today())}>Today</button>}
          <span className="meta">Leave blank while it's still at the shop.</span>
        </div>
      </div>

      <div>
        <label className="field-label" htmlFor="sv-notes">NOTES</label>
        <textarea id="sv-notes" className="control" rows={2} style={{ width: '100%' }} value={notes}
          onChange={(e) => setNotes(e.target.value)} />
      </div>
    </SlideOver>
  );
}

/** Adding a shop to the list, or correcting one. */
export function ShopForm({ shop, onClose, onSaved }: { shop?: Shop; onClose: () => void; onSaved?: (shopId: number) => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(shop?.name ?? '');
  const [address, setAddress] = useState(shop?.address ?? '');
  const [phone, setPhone] = useState(shop?.phone ?? '');
  const [email, setEmail] = useState(shop?.email ?? '');
  const [notes, setNotes] = useState(shop?.notes ?? '');
  const [isActive, setIsActive] = useState(shop?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body: ShopWrite = {
        name: name.trim(), address: address.trim() || null, phone: phone.trim() || null,
        email: email.trim() || null, notes: notes.trim() || null, isActive, rowVersionNo: shop?.rowVersionNo,
      };
      if (shop) { await api.put(`/api/shops/${shop.shopId}`, body); return shop.shopId; }
      return (await api.post<{ shopId: number }>('/api/shops', body)).shopId;
    },
    onSuccess: (id) => { invalidateAll(queryClient); if (onSaved) onSaved(id); else onClose(); },
    onError: (e: Error) => (e instanceof ConflictError ? setConflict(e.message) : setError(e.message)),
  });

  return (
    <SlideOver
      title={shop ? shop.name : 'Add a shop'}
      onClose={onClose}
      footer={(
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn--sm" onClick={onClose}>Cancel</button>
          <button className="btn btn--sm btn--primary" disabled={!name.trim() || save.isPending}
            onClick={() => { setError(null); save.mutate(); }}>
            {save.isPending ? 'Saving…' : shop ? 'Save' : 'Add shop'}
          </button>
        </div>
      )}
    >
      {conflict && <ConflictBanner message={conflict} onReload={() => { invalidateAll(queryClient); onClose(); }} />}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      <div style={{ marginBottom: 14 }}>
        <label className="field-label" htmlFor="sh-name">NAME</label>
        <input id="sh-name" className="control" style={{ width: '100%' }} value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label className="field-label" htmlFor="sh-address">ADDRESS</label>
        <textarea id="sh-address" className="control" rows={2} style={{ width: '100%' }} value={address}
          placeholder={'Street\nTown, ST zip'} onChange={(e) => setAddress(e.target.value)} />
        <div className="meta">Each branch is its own shop — Rush in Columbus and Rush in Indianapolis are two.</div>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <label className="field-label" htmlFor="sh-phone">PHONE</label>
          <input id="sh-phone" className="control" value={phone} inputMode="tel" onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label className="field-label" htmlFor="sh-email">EMAIL</label>
          <input id="sh-email" className="control" style={{ width: '100%' }} value={email} inputMode="email"
            onChange={(e) => setEmail(e.target.value)} />
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label className="field-label" htmlFor="sh-notes">NOTES</label>
        <textarea id="sh-notes" className="control" rows={2} style={{ width: '100%' }} value={notes}
          placeholder="Who to ask for, hours, what they're good for" onChange={(e) => setNotes(e.target.value)} />
      </div>
      {shop && (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Still used — untick to take it off the list for new visits. Its past visits stay.
        </label>
      )}
    </SlideOver>
  );
}
